// @flow
import React from 'react';
import Typography from '@material-ui/core/Typography';
import { withTranslation } from 'react-i18next';

import * as Sentry from '@sentry/browser';
import LogRocket from 'logrocket';
import type { RouterHistory } from 'react-router-dom';
import { withRouter } from 'react-router-dom';
import { withTheme, withStyles } from '@material-ui/core/styles';
import { authInstance } from '../../firebase';
import { getSearchValue } from '../../utils/http';
import UserSearchContainer from '../../components/UserSearchContainer';
import Loading from '../../components/Loading';
import { createUserSearchSocket } from '../../utils/getSocket';
import Footer from '../../components/Footer';
import { logoutAndClean } from '../../utils/user';
import electron from '../../utils/electron';

const branch = require('branch-sdk');

const styles = {
  innerWrap: {
    paddingBottom: 56,
    paddingTop: 96,
    maxWidth: 600,
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'column',
  }
}

type AuthenticationError = {
  name: string,
  message: string,
};

type Props = {
  backendSupport: Array<string>,
  backends: Object,
  branchId: string,
  displayable: boolean,
  classes: Object,
  enableAudioMode: Function,
  startRecord: Function,
  error: AuthenticationError,
  fetchAsyncBackendIfNeeded: Function,
  fetchBackendSupport: Function,
  fetchFeatureFlag: Function,
  firebaseLoginSuccess: Function,
  getBranchIOData: Function,
  history: RouterHistory,
  product: string,
  scribe: string,
  switchPlatform: Function,
  t: Object,
  toggleDevMode: Function,
  token: string,
};

type State = {
  connection: boolean,
  findByAvaName?: string,
  supportGuest: boolean,
  loading: boolean,
};

class ScribeWelcome extends React.Component<Props, State> {
  firebaseListenerUnsubscribe: Function;

  constructor(props: Props) {
    super(props);
    this.state = {
      connection: false,
      findByAvaName: undefined,
      supportGuest: false,
      loading: true,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.backendSupport.includes('connectRequest')) {
      this.setState({ supportGuest: true });
    }
  }

  componentDidMount = async () => {
    // HUGE HACK FIX ME TODO, have seperate html pages/bundles for webapp scribe app
    // $FlowFixMe
    document.querySelector('html').style.fontSize = 'inherit';

    const {
      branchId,
      fetchAsyncBackendIfNeeded,
      fetchBackendSupport,
      fetchFeatureFlag,
      firebaseLoginSuccess,
      getBranchIOData,
      product,
      scribe,
      token,
    } = this.props;

    if (localStorage.getItem('firebase:host:ava-debug-e2dcc.firebaseio.com')) {
      await logoutAndClean();
      window.history.pushState({}, document.title, '/#/scribe/login');
      window.location.reload();
    }

    fetchBackendSupport();
    fetchAsyncBackendIfNeeded();

    if (token && getSearchValue(window, 'token') !== token) {
      document.location.hash += `?token=${token}`;
      if (scribe && getSearchValue(window, 'scribe') !== token) {
        document.location.hash += `&scribe=${scribe}`;
      }
    }

    const fiiSessionId = localStorage.getItem('fii-session');
    localStorage.removeItem('fii-session');
    const request = localStorage.getItem('ava_request');
    if (request) {
      // localStorage.removeItem('ava_request');
      this.setState({ findByAvaName: request });
    }

    if (
      (token || branchId || request || fiiSessionId) &&
      localStorage.getItem('avaAccount') !== 'true'
    ) {
      authInstance.signInAnonymously();
    }

    createUserSearchSocket();


    let resBranch;
    
    const handleBranchInit = async () => {
      try {
        resBranch = await new Promise((res, rej) =>
          branch.init(
            'key_live_penYi35ThCyWIhmocnHM4piotqcldd1Y',
            { branch_match_id: branchId, no_journeys: false },
            (err, data) => {
              if (err) rej(err);
              else {
                if (data.data_parsed.backendEndpoint) {
                  localStorage.setItem(
                    'avaBackendUrl',
                    data.data_parsed.backendEndpoint
                  );
                }
                if (data.data_parsed.hostAvaId) {
                  localStorage.setItem(
                    'ava_request',
                    data.data_parsed.hostAvaId
                  );
                }
                if (data.data_parsed.audio) {
                  const { enableAudioMode, startRecord } = this.props;
                  enableAudioMode();
                  startRecord();
                }
                res(data.data_parsed);
              }
            }
          )
        );
      } catch (e) {
        // Branch is not a must have and is blocked
        // on firefox incognito so we just log the error
        console.log('Could not get branch data: ', e);
      }
    };
    // We launch branch even if not logged
    // in so journey banner is shown
    handleBranchInit();

    this.firebaseListenerUnsubscribe = authInstance.onAuthStateChanged(async user => {

      if (user && (user.emailVerified || user.isAnonymous || user.phoneNumber)) {
        const {
          displayName,
          email,
          emailVerified,
          photoURL,
          uid,
          isAnonymous,
        } = user;
        Sentry.addBreadcrumb({
          category: 'auth',
          message: JSON.stringify({
            displayName,
            email,
            emailVerified,
            photoURL,
            uid,
            isAnonymous,
          }),
          level: Sentry.Severity.Info,
        });

        const isNew =
          localStorage.getItem('avaAccount') !== 'true' && isAnonymous;
        user.getIdToken().then(idToken => {
          firebaseLoginSuccess(
            {
              displayName,
              email,
              emailVerified,
              photoURL,
              idToken,
              uid,
              isAnonymous,
            },
            isNew
          );
        });

        localStorage.setItem('avaAccount', 'true');
        handleBranchInit();
        getBranchIOData(resBranch);

        if (fiiSessionId) {
          try {
            const response = await fetch(
              `https://fii.ava.me/as/${fiiSessionId}.json`
            );
            const avaName = await response.json();
            if (avaName) {
              console.log('avaname found', avaName);
              localStorage.setItem('ava_request', avaName);
            }
          } catch (e) {
            console.error(e);
            console.log('redirecting to default...');
            window.open('/placeholder.html', '_self');
          }
        }

        let avaUser;
        if (!isAnonymous) {
          avaUser = await fetchFeatureFlag(product);
        }

        Sentry.configureScope(scope => {
          scope.setUser({ email, id: uid });
        });

        const backEndUrl = localStorage.getItem('scribeBackendUrl') || '';
        if (
          backEndUrl.includes('playground') ||
          backEndUrl.includes('localhost') ||
          backEndUrl.includes('ngrok')
        ) {
          LogRocket.identify(uid, {
            email,
          });
        }

        await this.switchProduct(avaUser || {});
      } else if (!user) {
        this.setState({ loading: false });
      }
    });
  };

  switchProduct = async user => {
    const { findByAvaName } = this.state;
    const { switchPlatform, history, product, token, branchId } = this.props;
    const { features } = user;
    let startConvo = false;
    if (history.location.search.includes('startConvo=true')) {
      // Let's hide the search startConvo path
      history.replace(history.location.pathname);
      startConvo = true;
    }
    // Let's hide the search startConvo path
    history.replace(history.location.pathname);
    await this.setState({ connection: true });

    if (
      features &&
      features.scribe &&
      !token &&
      !localStorage.getItem('currentMode') === 'scribe'
    ) {
      return switchPlatform('web', history);
    }
    if (
      features &&
      ((features.scribe &&
        product === 'scribe' &&
        features['scribe-roger-voice']) ||
        (!features['ava-webapp'] && features.scribe))
    ) {
      return switchPlatform('scribe', history);
    }
    if (
      (features && features['ava-webapp']) ||
      token ||
      branchId ||
      localStorage.getItem('ava_request')
    ) {
      return switchPlatform('web', history, { startConvo });
    }
    if (findByAvaName || user) {
      localStorage.setItem('ava_request', findByAvaName || '');
      return switchPlatform('web', history, {
        request: { avaName: findByAvaName },
      });
    }
    return history.push('/download');
  };

  onAvaName = (avaName: string) => {
    this.setState({ findByAvaName: avaName });
    authInstance.signInAnonymously();
  };

  componentWillUnmount() {
    if (this.firebaseListenerUnsubscribe) {
      this.firebaseListenerUnsubscribe();
    }
  }

  render() {
    const {
      backends,
      branchId,
      classes,
      displayable,
      error,
      t,
      toggleDevMode,
      token,
    } = this.props;
    const {
      connection,
      supportGuest,
      loading,
    } = this.state;
    const anonymous =
      (token || branchId) && localStorage.getItem('avaAccount') !== 'true';
    let loadingText;
    if (connection && !error.name) {
      loadingText = t('loading3d');
    }
    if (loadingText || loading) {
      return <Loading text={loadingText} />;
    }

    let heightOffset = '';
    if (window.isElectron) {
      const { remote } = electron;
      if (remote.process.platform === 'darwin') {
        heightOffset = ' - 28px'
      } else {
        heightOffset = ' - 32px'
      }
    }

    return (
      <div
        style={{
          height: `calc(var(--vh, 1vh) * 100${heightOffset})`,
          overflow: 'scroll',
        }}
      >
        <div className={classes.innerWrap}>
          <div style={{ flexGrow: 1 }} />
          {!connection &&
            !anonymous &&
            !error.name && (
            <>
              {supportGuest && (
                <UserSearchContainer
                  onAvaName={avaName => this.onAvaName(avaName)}
                />
              )}

              {error.name && (
                <>
                  <Typography align="center">
                    <i
                      css={{ textalign: 'center' }}
                      className="material-icons"
                    >
                      offline_bolt
                    </i>
                  </Typography>
                  <Typography variant="subtitle1" align="right">
                    {error.name}
                  </Typography>
                </>
              )}
            </>
          )}
          <div style={{ flexGrow: 2 }} />
        </div>
        <Footer
          toggleDevMode={toggleDevMode}
          displayable={displayable}
          backends={backends}
        />
      </div>
    );
  }
}

// $FlowFixMe
export default withTranslation()(withTheme(withStyles(styles)(withRouter(ScribeWelcome))));
